import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '@core/services/api.service';
import { HeaderService } from '@core/services/header.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { PermissionService } from '@core/services/permission.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';

@Component({
  standalone: false,
  selector: 'app-departments',
  templateUrl: './departments.component.html',
  styleUrls: ['./departments.component.scss']
})
export class DepartmentsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  departments: any[] = [];
  employees: any[] = [];
  empNameFn = (e: any) => e ? `${e.firstName || ''} ${e.lastName || ''}`.trim() : '';
  teams: any[] = [];
  shifts: any[] = [];
  teamMembersMap: { [teamId: string]: any[] } = {};
  loading = true;
  submitting = false;

  activeTab = 'list';
  selectedDept: any = null;
  selectedTeamInDept: any = null;

  form: any = { name: '', description: '', headId: '', color: '#8b1a1a' };
  showEditDialog = false;
  showCreateDept = false;
  editForm: any = {};

  constructor(
    private apiService: ApiService,
    private headerService: HeaderService,
    private snackbarService: SnackbarService,
    public permissions: PermissionService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('Departments');
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    forkJoin({
      departments: this.apiService.get('/departments').pipe(catchError(() => of([]))),
      employees: this.apiService.get('/employees').pipe(catchError(() => of([]))),
      teams: this.apiService.get('/teams').pipe(catchError(() => of([]))),
      shifts: this.apiService.get('/shifts').pipe(catchError(() => of([])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.departments = this.extractList(data.departments);
        this.employees = this.extractList(data.employees);
        this.teams = this.extractList(data.teams);
        this.shifts = this.extractList(data.shifts);
        this.loading = false;
        if (this.departments.length > 0) this.selectedDept = this.departments[0];
        this.loadAllTeamMembers();
      },
      error: () => { this.loading = false; }
    });
  }

  private loadAllTeamMembers(): void {
    if (this.teams.length === 0) return;
    const requests = this.teams.map(t =>
      this.apiService.get('/teams/' + t.id + '/members').pipe(catchError(() => of([])))
    );
    forkJoin(requests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (responses: any[]) => {
        const map: { [teamId: string]: any[] } = {};
        responses.forEach((res, i) => {
          const members = this.extractList(res);
          map[this.teams[i].id] = members
            .filter((m: any) => m?.employeeId)
            .map((m: any) => this.employees.find(e => e.id === m.employeeId))
            .filter(Boolean);
        });
        this.teamMembersMap = map;
      },
      error: () => { this.teamMembersMap = {}; }
    });
  }

  private extractList(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  selectDept(dept: any): void {
    if (this.selectedDept?.id === dept.id) {
      this.selectedDept = null;
      this.selectedTeamInDept = null;
    } else {
      this.selectedDept = dept;
      this.selectedTeamInDept = null;
    }
  }

  selectTeamInDept(team: any): void {
    this.selectedTeamInDept = this.selectedTeamInDept?.id === team.id ? null : team;
  }

  getHead(headId: string): any {
    return this.employees.find(e => e.id === headId) || null;
  }

  isActive = (e: any) => {
    const s = (e?.status || '').toLowerCase();
    const r = (e?.role || '').toLowerCase().replace(/^role_/, '');
    return s !== 'inactive' && r !== 'admin' && r !== 'super_admin';
  };

  getEmployeeCount(deptId: string): number {
    const fromDeptField = this.employees.filter(e => e.departmentId === deptId && this.isActive(e)).map(e => e.id);
    const fromTeams = this.getDeptTeams(deptId)
      .flatMap(t => (this.teamMembersMap[t.id] || []).filter((e: any) => this.isActive(e)).map((e: any) => e?.id).filter(Boolean));
    return new Set([...fromDeptField, ...fromTeams]).size;
  }

  getDeptEmployees(deptId: string): any[] {
    const fromDeptField = this.employees.filter(e => e.departmentId === deptId && this.isActive(e));
    const fromTeams = this.getDeptTeams(deptId)
      .flatMap(t => (this.teamMembersMap[t.id] || []).filter((e: any) => this.isActive(e)));
    const seen = new Set<string>();
    return [...fromDeptField, ...fromTeams].filter(e => {
      if (seen.has(e.id)) return false;
      seen.add(e.id); return true;
    }).slice(0, 3);
  }

  getDeptTeams(deptId: string): any[] {
    return this.teams.filter(t => t.departmentId === deptId);
  }

  deptEmployeesFull(deptId: string): any[] {
    const fromDeptField = this.employees.filter(e => e.departmentId === deptId && this.isActive(e));
    const fromTeams = this.getDeptTeams(deptId).flatMap(t => (this.teamMembersMap[t.id] || []).filter((e: any) => this.isActive(e)));
    const seen = new Set<string>();
    return [...fromDeptField, ...fromTeams].filter(e => { if (!e || seen.has(e.id)) return false; seen.add(e.id); return true; });
  }

  getTeamLeadName(leadId: string): string {
    if (!leadId) return '—';
    const emp = this.employees.find(e => e.id === leadId);
    return emp ? `${emp.firstName} ${emp.lastName}` : '—';
  }

  getTeamShiftName(shiftId: string): string {
    if (!shiftId) return 'General';
    const shift = this.shifts.find(s => s.id === shiftId);
    return shift ? shift.name : 'General';
  }

  getTeamMembers(teamId: string): any[] {
    return this.teamMembersMap[teamId] || [];
  }

  getTeamMemberCount(teamId: string): number {
    return (this.teamMembersMap[teamId] || []).length;
  }

  getTeamMemberAvatars(teamId: string): any[] {
    return (this.teamMembersMap[teamId] || []).slice(0, 3);
  }

  getInitials(emp: any): string {
    return `${(emp.firstName || '?').charAt(0)}${(emp.lastName || '?').charAt(0)}`;
  }

  getAvatarColor(emp: any): string {
    const colors = ['#334155', '#475569', '#0f766e', '#166534', '#92400e', '#7c3a5a', '#3f4a5f', '#5b6472'];
    const name = (emp.firstName || '') + (emp.lastName || '');
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  submitDepartment(): void {
    if (!this.form.name) return;
    this.submitting = true;
    this.apiService.post('/departments', this.form).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snackbarService.showSuccess('Department created');
        this.form = { name: '', description: '', headId: '', color: '#8b1a1a' };
        this.submitting = false;
        this.showCreateDept = false;
        this.loadData();
      },
      error: () => { this.snackbarService.showFailure('Failed to create department'); this.submitting = false; }
    });
  }

  openEditDept(dept: any): void {
    this.editForm = { id: dept.id, name: dept.name, description: dept.description || '', headId: dept.headId || '', color: dept.color || '#8b1a1a' };
    this.showEditDialog = true;
  }

  saveEditDept(): void {
    if (!this.editForm.name) return;
    this.submitting = true;
    const { id, ...payload } = this.editForm;
    this.apiService.patch('/departments/' + id, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        const idx = this.departments.findIndex(d => d.id === id);
        if (idx !== -1) this.departments[idx] = { ...this.departments[idx], ...payload };
        if (this.selectedDept?.id === id) this.selectedDept = { ...this.selectedDept, ...payload };
        this.snackbarService.showSuccess('Department updated');
        this.showEditDialog = false;
        this.submitting = false;
      },
      error: () => { this.snackbarService.showFailure('Failed to update'); this.submitting = false; }
    });
  }

  deleteDepartment(id: string): void {
    this.confirmDialog.confirm('This department and its team assignments will be permanently deleted.', {
      title: 'Delete Department', confirmLabel: 'Delete', danger: true
    }).then(ok => {
      if (!ok) return;
      this.apiService.delete('/departments/' + id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.snackbarService.showSuccess('Deleted');
          this.selectedDept = null;
          this.selectedTeamInDept = null;
          this.loadData();
        },
        error: () => { this.snackbarService.showFailure('Failed to delete'); }
      });
    });
  }
}
